import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, switchMap } from 'rxjs';
import { WebformEnum } from '../../enum/webform.enum';
import {
  Webform,
  WebformAuditReport,
  WebformCancellation,
  WebformCircularCheck,
  WebformComplaints,
  WebformContactManager,
  WebformDeclarationSupport,
  WebformEuDesk,
  WebformFeedback,
  WebformGenericTaskCreation,
  WebformPackagingManagementAdhesion,
  WebformPaymentFlexibility,
  WebformProducerRegistrySupport,
  WebformRequestChangeAddress,
  WebformRequestChangeGeneralData,
  WebformResponse,
  WebformSirapConstitution,
  WebformSocietyChange,
} from '../../types/webform.type';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root',
})
export class WebformService extends ApiService<any> {
  constructor() {
    super();
    this.moduleUrl.set('/webform_rest');
  }

  submit(body: Webform, context?: HttpContext): Observable<WebformResponse> {
    return this.http.post<WebformResponse>(`${this.url()}/submit`, body, {
      ...(context ? { context } : {}),
    });
  }

  uploadFile(
    webformId: string,
    file: File,
    fileField: string,
    context?: HttpContext,
  ) {
    const headers = {
      'Content-Type': 'application/octet-stream',
      Accept: 'application/vnd.api+json',
      'Content-Disposition': `file; filename="${this.sanitizeFilename(file.name)}"`,
    };

    return this.http.post(
      `${this.url()}/${webformId}/upload/${fileField}`,
      file,
      {
        headers,
        ...(context ? { context } : {}),
      },
    );
  }

  /**
   * Sanitiza el nombre del archivo removiendo caracteres no ISO-8859-1
   * para el parámetro filename
   */
  private sanitizeFilename(filename: string): string {
    return filename
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\x00-\xFF]/g, '_');
  }

  /**
   * Método genérico para enviar webforms con archivo opcional
   * @param body - Cuerpo del webform
   * @param file - Archivo opcional a subir
   * @param webformId - ID del webform
   * @param fileField - Nombre del campo del archivo en el webform
   * @returns Observable con la respuesta del webform
   */
  private submitWithOptionalFile<T extends Webform>(
    body: T,
    files: File[] | File | null | undefined,
    webformId: string,
    fileField: string,
    context?: HttpContext,
  ): Observable<WebformResponse> {
    if (files) {
      if (Array.isArray(files)) {
        // Subir todos los archivos y recoger los ids usando forkJoin
        return forkJoin(
          files.map((file) =>
            this.uploadFile(webformId, file, fileField, context),
          ),
        ).pipe(
          switchMap((responses: any[]) => {
            const fileIds = responses.map(
              (response) => response?.fid?.[0]?.value || '',
            );
            (body as any)[fileField] = fileIds;
            return this.submit(body, context);
          }),
        );
      } else {
        return this.uploadFile(webformId, files, fileField, context).pipe(
          switchMap((response: any) => {
            const fileId = response?.fid?.[0]?.value;
            (body as any)[fileField] = fileId || '';
            return this.submit(body, context);
          }),
        );
      }
    }

    return this.submit(body, context);
  }

  contactManager(form: Partial<WebformContactManager>, file?: File) {
    const body: WebformContactManager = {
      webform_id: WebformEnum.CONTACT_MANAGER,
      nombre_gestor: '',
      nombre_cliente: '',
      mensaje: '',
      documento: '',
      email_gestor: '',
      email_cliente: '',
      asunto: '',
      nombre_empresa: '',
      cif: '',
      ...form,
    };

    return this.submitWithOptionalFile(
      body,
      file,
      WebformEnum.CONTACT_MANAGER,
      'documento',
    );
  }

  updateCompanyData(form: Partial<WebformRequestChangeGeneralData>) {
    const body: WebformRequestChangeGeneralData = {
      webform_id: WebformEnum.REQUEST_CHANGE_GENERAL_DATA,
      ...form,
    };

    return this.submit(body);
  }

  updateCompanyAddress(form: Partial<WebformRequestChangeAddress>) {
    const body: WebformRequestChangeAddress = {
      webform_id: WebformEnum.REQUEST_CHANGE_ADDRESS,
      ...form,
    };

    return this.submit(body);
  }

  feedback(webformRequest: Partial<WebformFeedback>, file: File | null) {
    const body: WebformFeedback = {
      webform_id: WebformEnum.FEEDBACK,
      ...webformRequest,
    };

    return this.submitWithOptionalFile(
      body,
      file,
      WebformEnum.FEEDBACK,
      'file',
    );
  }

  requestDuplicateCoordinatedCard() {
    const body = {
      webform_id: WebformEnum.DUPLICATE_COORDINATED_CARD,
    };

    return this.submit(body);
  }

  requestCancellation(form: Partial<WebformCancellation>, file: File | null) {
    const body = {
      webform_id: WebformEnum.CANCELLATION,
      ...form,
    };

    return this.submitWithOptionalFile(
      body,
      file,
      WebformEnum.CANCELLATION,
      'file',
    );
  }

  requestSocietyChange(form: Partial<WebformSocietyChange>, file: File | null) {
    const body = {
      webform_id: WebformEnum.SOCIETY_CHANGE,
      ...form,
    };

    return this.submitWithOptionalFile(
      body,
      file,
      WebformEnum.SOCIETY_CHANGE,
      'file',
    );
  }

  requestPaymentFlexibility(form: Partial<WebformPaymentFlexibility>) {
    const body: WebformPaymentFlexibility = {
      webform_id: WebformEnum.PAYMENT_FLEXIBILITY,
      nombre_usuario: '',
      email_usuario: '',
      tipo_flexibilizacion: '',
      comentarios: '',
      ...form,
    };

    return this.submit(body);
  }

  requestUpToDateWithObligations() {
    const body = {
      webform_id: WebformEnum.UP_TO_DATE_WITH_OBLIGATIONS,
    };

    return this.submit(body);
  }

  requestAdhesionToBusinessPlans() {
    const body = {
      webform_id: WebformEnum.ADHESION_TO_BUSINESS_PLANS,
    };

    return this.submit(body);
  }

  requestClaim(form: Partial<WebformComplaints>, file: File | null) {
    const body = {
      webform_id: WebformEnum.COMPLAINTS,
      ...form,
    };

    return this.submitWithOptionalFile(
      body,
      file,
      WebformEnum.COMPLAINTS,
      'file',
    );
  }

  requestBankDataChange(files: File[] | null) {
    const body = {
      webform_id: WebformEnum.BANK_DATA,
    };

    return this.submitWithOptionalFile(
      body,
      files,
      WebformEnum.BANK_DATA,
      'file',
    );
  }

  requestAuditReport(
    form: Partial<WebformAuditReport>,
    file: File | null,
    context?: HttpContext,
  ) {
    const body = {
      webform_id: WebformEnum.AUDIT_REPORT,
      ...form,
    };

    return this.submitWithOptionalFile(
      body,
      file,
      WebformEnum.AUDIT_REPORT,
      'file',
      context,
    );
  }

  createGenericTask(form: Partial<WebformGenericTaskCreation>) {
    const body = {
      webform_id: WebformEnum.CREATION_GENERIC_TASK,
      ...form,
    };

    return this.submit(body);
  }

  // Servicios

  // Constitución de un SIRAP
  requestSirapConstitution(
    form: Partial<WebformSirapConstitution>,
    file: File | null,
  ) {
    const body = {
      webform_id: WebformEnum.SIRAP_CONSTITUTION,
      ...form,
    };

    return this.submitWithOptionalFile(
      body,
      file,
      WebformEnum.SIRAP_CONSTITUTION,
      'file',
    );
  }

  // Asistencia en el reporte al Registro de productores
  requestProducerRegistrySupport(
    form: Partial<WebformProducerRegistrySupport>,
    file: File | null,
  ) {
    const body = {
      webform_id: WebformEnum.PRODUCER_REGISTRY_SUPPORT,
      ...form,
    };

    return this.submitWithOptionalFile(
      body,
      file,
      WebformEnum.PRODUCER_REGISTRY_SUPPORT,
      'file',
    );
  }

  // DECLARATION SUPPORT WEBFORMS

  requestDeclarationSupport(
    form: Partial<WebformDeclarationSupport>,
    file: File | null,
  ) {
    return this.submitWithOptionalFile(
      form as WebformDeclarationSupport,
      file,
      WebformEnum.DECLARATION_SUPPORT_BASIC,
      'file',
    );
  }

  // CIRCULAR CHECK WEBFORMS
  requestCircularCheck(form: Partial<WebformCircularCheck>) {
    const body = {
      webform_id: WebformEnum.CIRCULAR_CHECK,
      ...form,
    };

    return this.submit(body);
  }

  // EU DESK WEBFORMS
  requestEuDesk(form: Partial<WebformEuDesk>) {
    return this.submit(form as WebformEuDesk);
  }

  // ADHESIÓN GESTIÓN DE ENVASES
  requestPackagingManagementAdhesion(
    form: Partial<WebformPackagingManagementAdhesion>,
  ) {
    const body = {
      webform_id: WebformEnum.PACKAGING_MANAGEMENT_ADHESION,
      ...form,
    };

    return this.submit(body);
  }
}
